import { useCallback, useMemo } from 'react'
import { useContactBulkActions } from 'app/components/contacts/useContactBulkActions'
import { BulkActionBar } from 'app/components/table/bulkActions/BulkActionBar'
import type {
  BulkAction,
  BulkActionOption,
  BulkActionValue,
  BulkActionsConfig,
  BulkProgressReporter,
  BulkSelection,
} from 'app/components/table/bulkActions/types'
import type { ContactJson } from 'app/types/entities'
import { SetStage, applyStageChange } from './treatmentBulkActions'

/** Not a contact field, so its own id. */
const BOARD_STAGE_ACTION_ID = `__boardStage`

/**
 * A contact board's bulk bar: the board's own stage move, then the contacts table's set.
 * Mounted only while selecting, so the pipelines and workflows that set reads are fetched
 * only once somebody asks for the bar.
 */
export function ContactBoardBulkBar({
  selection,
  stages,
  omitLeadStatus,
  setStage,
  onExit,
}: {
  selection: BulkSelection<ContactJson>
  /** A stage that needs more than a move (Closed Lost asks for a reason) is the caller's to leave out. */
  stages: BulkActionOption[]
  /**
   * Set where the stages are the lead status: "Set lead status" would write it behind the
   * board's back, so no card would move and Closed Lost would land without its reason.
   */
  omitLeadStatus: boolean
  setStage: SetStage<ContactJson>
  onExit: () => void
}) {
  const contacts = useContactBulkActions()

  const actions = useMemo<BulkAction[]>(() => {
    const stageAction: BulkAction = {
      id: BOARD_STAGE_ACTION_ID,
      label: `Move to stage`,
      control: `select`,
      options: stages,
      placeholder: `Choose a stage…`,
    }
    const shared = omitLeadStatus ? contacts.actions.filter((action) => action.id !== `leadStatus`) : contacts.actions
    return [stageAction, ...shared]
  }, [stages, omitLeadStatus, contacts.actions])

  const apply = useCallback(
    (actionId: string, value: BulkActionValue, rows: ContactJson[], onProgress?: BulkProgressReporter) =>
      actionId === BOARD_STAGE_ACTION_ID
        ? applyStageChange(rows, value as string, setStage, onProgress)
        : contacts.apply(actionId, value, rows, onProgress),
    [contacts, setStage],
  )

  const config = useMemo<BulkActionsConfig<ContactJson>>(() => ({ actions, apply }), [actions, apply])

  return <BulkActionBar selection={selection} config={config} onExit={onExit} />
}
